import { checkDeliveryCoverage, setConveyance, CONVEYANCE_TYPES } from '@koala/sdk/v4';
import Router from 'next/router';
import { call, put, race, select, take, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import actions from './actions';
import { fireGACommerceAddShippingInfoEvent } from '@/analytics/commerce/google';
import { API_CONVEYANCE_TYPES, ERROR_MESSAGES } from '@/constants/events';
import { coverageErrorEventName, coverageErrorMessage } from '@/constants/coverageErrorCopy';
import { ASAP_VALUE, DELIVERY_TIME_WANTED_MODES } from '@/constants/global';
import { ROUTES } from '@/constants/routes';
import { getMappedConveyanceType } from '@/features/handoff/time-picker/utils';
import basketActions from '@/redux/basket/actions';
import globalActions from '@/redux/global/actions';
import menuActions from '@/redux/menu/actions';
import { surfaceConveyanceGatewayFailure } from '@/redux/conveyanceMode/gatewayFailure';
import orderStatusActions from '@/redux/orderStatus/actions';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { OrderStatuses } from '@/types/orderStatus';
import { getOrigin } from '@/utils';
import { userLoggedIn } from '@/utils/auth';
import { assembleDeliveryObjectFromState, getIdsFromState } from '@/utils/checkout';
import { buildFulfillmentFromBasket, coerceAddressValidationPayload } from '@/utils/fulfillment';
import { getUnavailableBasketItemIds } from '@/utils/errors';
import { GATEWAY_STATUSES, isStatusError } from '@/utils/networkErrors';
import { retryOnceOnStatuses } from '@/utils/requests';
import { prepareErrorMessage, sessionStorageBasket } from '@/utils/global';
import { getLocationId } from '@/utils/locations';
import { DELIVERY_HANDOFF_TYPE_DEFAULT } from '@/constants/conveyance';
import * as ErrorReporter from '@/utils/errorReporter';

export function* checkDeliveryAddressSaga(action: ReturnType<typeof actions.checkDeliveryAddress>) {
  try {
    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });

    const coercedAddress = coerceAddressValidationPayload(action.address);
    const response: SagaReturnType<typeof checkDeliveryCoverage> = yield call(
      checkDeliveryCoverage,
      action.location.id,
      coercedAddress,
      { client },
    );
    // If valid
    if (response?.data.can_deliver) {
      yield put(
        actions.setDeliveryAddress(
          action.address,
          action.location,
          action.revalidate,
          action.shouldAutoRedirect,
        ),
      );
      yield put({ type: globalActions.DELIVERY_ADDRESS_VALID });
    } else {
      // If not valid
      yield put({
        type: globalActions.DELIVERY_ADDRESS_INVALID,
        errorMessage: response?.data.message,
      });

      ErrorReporter.captureException(new Error('Delivery address failed coverage check'), 'error', {
        details: response?.data.message,
        eventName: ERROR_MESSAGES.CHECK_DELIVERY_COVERAGE_ERROR,
      });

      yield put({ type: actions.CHECK_DELIVERY_ADDRESS_FAILURE });
    }
  } catch (error) {
    yield put({ type: actions.CHECK_DELIVERY_ADDRESS_FAILURE });
    // Same copy as the menu-availability path (delivery-details.tsx) — AC #9
    // requires a failed check to read identically wherever the diner meets it.
    yield put(globalActions.displayErrorToast(coverageErrorMessage(error), true));

    // KA event
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );

    ErrorReporter.captureException(error, 'error', {
      extra: errorResponse,
      eventName: coverageErrorEventName(error),
    });
  }
}

function* setDeliveryAddressSaga(action: ReturnType<typeof actions.setDeliveryAddress>) {
  const state: RootState = yield select();
  const { basketId } = getIdsFromState(state);
  const storedAddressesEnabled = state.app.cmsConfig.webConfig.accounts.stored_addresses;

  // If stored addresses are enabled in the web config AND user is logged in
  const useAddressId = storedAddressesEnabled && userLoggedIn() ? true : false;
  const addressObject = Object.assign({}, action.address);
  addressObject.hand_off_type =
    typeof action.address.hand_off_type === 'number'
      ? action.address.hand_off_type
      : DELIVERY_HANDOFF_TYPE_DEFAULT;

  try {
    const koalaOrderJSON = sessionStorageBasket.get();

    if (koalaOrderJSON && koalaOrderJSON.location.id === action.location.id) {
      // Delete address id and stale_id from payload if not relevant for stored addresses
      if (!useAddressId) {
        /** @TODO unravel type issues with the `id` and `stale_id`. */
        delete addressObject.id;
        delete addressObject.stale_id;
      }

      // Reset conveyance on existing baskets
      yield put(
        basketActions.updateBasketFulfillment({
          address: addressObject,
          type: CONVEYANCE_TYPES.DELIVERY,
        }),
      );

      if (basketId) {
        const currentDeliveryInstructions = addressObject.special_instructions;

        yield put(
          orderStatusActions.conveyanceDeliverySet(
            currentDeliveryInstructions,
            addressObject.stale_id,
          ),
        );
      }
      // If basket exists AND the basket is the same location as the new fulfillment, re-update the basket
      yield put(
        basketActions.updateBasketFulfillment({
          address: addressObject,
          type: CONVEYANCE_TYPES.DELIVERY,
        }),
      );
      yield put(actions.setDeliveryAddressSuccess(addressObject, action.location.id));
      yield put(actions.setConveyanceLoading(false));
    } else {
      yield put(actions.setDeliveryAddressSuccess(addressObject, action.location.id));

      const state: RootState = yield select();
      if (state.app.delivery?.deliveryType?.type) {
        fireGACommerceAddShippingInfoEvent(
          state.app.basket,
          state.app.menu.basketMenu,
          state.app.promoCode.applied,
          state.app.delivery?.deliveryType?.type,
        );
      }
    }

    if (action.shouldAutoRedirect) {
      // Automatically redirect the user to the correct store page.
      yield Router.push(
        ROUTES.STORE,
        `/store/${getLocationId(action.location, state.app.cmsConfig.webConfig)}/${
          action.location.label
        }`,
      );
    } else {
      // Wait for the user to either click the Continue button or close the modal.
      const { save } = yield race({
        save: take(actions.SET_DELIVERY_ADDRESS_CONTINUE),
        cancel: take(globalActions.TOGGLE_FULFILLMENT_MODAL),
      });

      // If they click continue, redirect them to the correct store page.
      if (save) {
        yield Router.push(
          ROUTES.STORE,
          `/store/${getLocationId(action.location, state.app.cmsConfig.webConfig)}/${
            action.location.label
          }`,
        );
      }
    }
    if (action.revalidate) {
      yield put({ type: globalActions.DELIVERY_ADDRESS_VALID });
    }
  } catch (error) {
    yield put({ type: actions.SET_DELIVERY_ADDRESS_FAILURE });

    // If error results from a stored address, record the address ID
    const state: RootState = yield select();
    const deliveryAddress = assembleDeliveryObjectFromState(state);
    let additionalDetails;
    if (deliveryAddress?.id) {
      additionalDetails = `Stored Address ID: ${deliveryAddress.id}`;
    }

    // KA event
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      additionalDetails,
      eventName: ERROR_MESSAGES.SET_DELIVERY_ADDRESS_ERROR,
    });
  }
}

export function* setWantedTimeSaga(action: ReturnType<typeof actions.setWantedTime>) {
  const state: RootState = yield select();
  const fulfillment = state.app.basket.fulfillment;

  try {
    const koalaOrderJSON = sessionStorageBasket.get();

    if (koalaOrderJSON && koalaOrderJSON.location.id === action.locationId) {
      if (action.conveyanceType === CONVEYANCE_TYPES.DELIVERY) {
        // Ensure we have a complete address object with all required properties
        const address = fulfillment?.address ?? {
          city: '',
          street_address: '',
          zip_code: '',
          time_wanted: null,
          time_wanted_mode: action.wantedAt
            ? DELIVERY_TIME_WANTED_MODES.ASAP
            : DELIVERY_TIME_WANTED_MODES.ADVANCE,
        };

        yield put(
          basketActions.updateBasketFulfillment({
            ...fulfillment,
            type: action.conveyanceType,
            address: address
              ? {
                  ...address,
                  time_wanted: action.wantedAt ?? null,
                }
              : undefined,
            time_wanted: '',
          }),
        );
      } else {
        yield put(
          basketActions.updateBasketFulfillment({
            ...fulfillment,
            address: fulfillment?.address
              ? {
                  ...fulfillment.address,
                  time_wanted: '',
                }
              : undefined,
            type: action.conveyanceType,
            time_wanted: action.wantedAt,
          }),
        );
      }
    }

    // Keep the Redux basket menu (state.app.menu.basketMenu) in sync with the
    // newly-selected handoff time. Without this, the cart drawer renders the
    // header with no items because basketProductsWithOptionPricing can't find
    // basket items in a stale menu after a time change.
    // Guard: skip the menu refresh if the location id hasn't resolved yet —
    // dispatching with undefined builds a request to store-locations/undefined/menu
    // which coo-api correctly 404s (TEAM-14239).
    if (action.locationId) {
      yield put(menuActions.refreshBasketMenu(action.locationId, action.wantedAt));
    }
    yield put(actions.setAttemptedWantedAt(null));

    // See wantedAtBasketSetSaga: the confirmation is dispatched from the success
    // branch only, so a failure below cannot be preceded by a success toast
    // (TEAM-14387 AC6). This path also serves the Restart-order confirm, which is
    // why AC7's toast fires after the diner confirms rather than before.
    // Scope of this guarantee, stated precisely: the commit itself has succeeded by
    // this point, and a failure *in this saga* cannot precede the toast. It does not
    // cover the menu refresh the second line mentions. refreshBasketMenu is dispatched,
    // not awaited, and its own recovery branch (redux/menu/saga.ts:72-89) responds to a
    // wanted-at validation error by dispatching setWantedTime(undefined) — clearing the
    // very time just confirmed. That window is narrow and the alternative (toasting from
    // the refresh's success) would not fire at all when no locationId is present, so it
    // is accepted rather than closed. Do not read the comment above as "the toast implies
    // the refresh completed".
    if (action.confirmation) {
      yield put(
        globalActions.displayToast(
          action.confirmation.message,
          undefined,
          undefined,
          action.confirmation.secondaryMessage,
        ),
      );
    }
  } catch (error) {
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      `Failed to set wanted time ${action.wantedAt} for location ${action.locationId}.`,
      error,
    );

    const unavailableBasketItemIds = getUnavailableBasketItemIds(errorResponse);
    if (unavailableBasketItemIds.length) {
      yield put(
        basketActions.setUnavailableBasketItems(
          unavailableBasketItemIds,
          errorResponse.error?.error,
        ),
      );
      yield put(actions.setAttemptedWantedAt(action.wantedAt ?? ASAP_VALUE));
    }

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.SET_WANTED_TIME_ERROR,
    });
  }
}

function* clearDeliveryAddressSaga(action: ReturnType<typeof actions.clearDeliveryAddress>) {
  try {
    const koalaOrderJSON = sessionStorageBasket.get();

    if (koalaOrderJSON && koalaOrderJSON.location.id === action.locationId) {
      // If basket exists, ensure this is also reset
      yield put({
        type: basketActions.UPDATE_BASKET_FULFILLMENT,
        fulfillment: {
          address: null,
          type: CONVEYANCE_TYPES.PICKUP,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          time_wanted: koalaOrderJSON.fulfillment?.address?.time_wanted,
        },
      });
    }
  } catch (error) {
    ErrorReporter.captureException(error, 'error', { details: (error as Error).message });
  }
}

export function* conveyanceSetSaga(action: ReturnType<typeof actions.conveyanceModeSet>) {
  try {
    // Wait for it to settle before firing another setConveyance, otherwise BE and FE can
    // diverge based on response ordering (TEAM-14698).
    const conveyanceInFlight: boolean = yield select(
      (state: RootState) =>
        state.app.conveyanceMode.loading ||
        state.app.orderStatus.status === OrderStatuses.CONVEYANCE_DELIVERY_SETTING,
    );
    const currentConveyanceType: string = yield select(
      (state: RootState) => state.app.conveyanceMode.type,
    );
    if (conveyanceInFlight || currentConveyanceType === action.conveyanceType) {
      return;
    }

    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const { basketId, locationId: basketLocationId } = yield select(getIdsFromState);

    // @ts-expect-error: sagas have terrible typing
    const locationId = yield select((state) => state.app.locations?.detail?.id);

    // We set conveyance on back-end only if we have basket created
    // Basket should be create when user navigates to checkout and signs in
    if (
      basketId &&
      basketLocationId &&
      (action.conveyanceType === API_CONVEYANCE_TYPES.PICKUP ||
        (action.conveyanceType === API_CONVEYANCE_TYPES.DELIVERY &&
          'street_address' in action.conveyanceDetails &&
          action.conveyanceDetails.phone_number))
    ) {
      if (action.conveyanceType === API_CONVEYANCE_TYPES.PICKUP) {
        yield put(orderStatusActions.conveyanceSet());
      } else {
        yield put(orderStatusActions.conveyanceDeliverySetting());
      }

      const basketResponse = yield* retryOnceOnStatuses(
        setConveyance,
        { statuses: GATEWAY_STATUSES },
        {
          basketId: basketId as string,
          locationId: basketLocationId as number,
          type: action.conveyanceType,
          // @ts-expect-error ensure that `conveyanceDetails` is defined.
          data: action.conveyanceDetails,
        },
        { client },
      );
      yield put(basketActions.success(basketResponse));

      const currentTimeWanted: string | undefined = yield select(
        (state: RootState) => state.app.basket.checkoutBasket?.wanted_at,
      );
      yield put(
        basketActions.updateBasketFulfillment(
          buildFulfillmentFromBasket(basketResponse, currentTimeWanted),
        ),
      );

      yield put(orderStatusActions.orderPending());
    }

    // Ensure the local basket wanted at time is valid
    yield put(
      actions.setWantedTime(
        action.wantedAt,
        action.locationId ?? basketLocationId ?? locationId,
        getMappedConveyanceType(action.conveyanceType),
      ),
    );

    // @IMPLEMENTATION-TODO:
    // remove in favor of hooks in `<FeatureAccessor />` after Next upgrade
    yield put(globalActions.generateFeatureBag());
  } catch (error) {
    // A transient gateway 502 (retried once above) leaves the conveyance
    // un-persisted on the remote basket, which later bricks submit with the
    // terminal "error setting your handoff mode" throw in checkout.ts. Surface
    // the recoverable 502 modal (cart preserved) instead of the dead end.
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield* surfaceConveyanceGatewayFailure(error);
      return;
    }
    yield put(actions.handleConveyanceFailure(error as Error, action.conveyanceType));
  }
}

export function* conveyanceDeliverySetSaga(
  action: ReturnType<typeof orderStatusActions.conveyanceDeliverySet>,
) {
  const client = createHttpClient({
    origin: getOrigin(window.location.host),
  });

  try {
    // Wait for an in-flight conveyance to settle TEAM-14698
    const conveyanceInFlight: boolean = yield select(
      (state: RootState) =>
        state.app.conveyanceMode.loading ||
        state.app.orderStatus.status === OrderStatuses.CONVEYANCE_SETTING,
    );
    const state: RootState = yield select();
    const { basketId, locationId } = getIdsFromState(state);
    if (conveyanceInFlight) {
      if (basketId) {
        yield put(orderStatusActions.orderPending());
      }
      return;
    }

    const deliveryAddress = assembleDeliveryObjectFromState(
      state,
      action.instructions,
      action.staleId,
    );

    // TODO update types
    let basketResponse: SagaReturnType<typeof setConveyance> = yield* retryOnceOnStatuses(
      setConveyance,
      { statuses: GATEWAY_STATUSES },
      {
        basketId,
        locationId,
        type: API_CONVEYANCE_TYPES.DELIVERY,
        // @ts-expect-error ensure that `deliveryAddress` is defined.
        data: deliveryAddress,
      },
      { client },
    );

    // Handle editing conveyance
    if (deliveryAddress?.stale_id) {
      // Create a new delivery address using the ID from the basket response
      const newDeliveryAddress = Object.assign({}, deliveryAddress, {
        id: basketResponse?.conveyance_type?.delivery_address?.id,
      });

      // Now delete the stale ID
      delete newDeliveryAddress.stale_id;
      // Trigger yet another basket response with the NEW address ID
      basketResponse = yield* retryOnceOnStatuses(
        setConveyance,
        { statuses: GATEWAY_STATUSES },
        {
          basketId,
          locationId,
          type: API_CONVEYANCE_TYPES.DELIVERY,
          // @ts-expect-error
          data: newDeliveryAddress,
        },
        { client },
      );

      // Take the latest basket response and format the data to update basket fulfillment with
      const basketDeliveryAddress = basketResponse?.conveyance_type?.delivery_address;
      const newBasketDeliveryAddress = Object.assign({}, basketDeliveryAddress, {
        zip_code: basketResponse?.conveyance_type?.delivery_address?.zip_code,
      });

      // Update basket fulfillment with the new address
      yield put({
        type: basketActions.UPDATE_BASKET_FULFILLMENT,
        fulfillment: {
          address: newBasketDeliveryAddress,
          type: CONVEYANCE_TYPES.DELIVERY,
        },
      });
    }
    yield put(basketActions.success(basketResponse));

    const currentTimeWanted: string | undefined = yield select(
      (state: RootState) => state.app.basket.checkoutBasket?.wanted_at,
    );
    yield put({
      type: basketActions.UPDATE_BASKET_FULFILLMENT,
      fulfillment: {
        type: getMappedConveyanceType(basketResponse?.conveyance_type?.type),
        address: {
          ...(basketResponse?.conveyance_type?.delivery_address ?? null),
          time_wanted: currentTimeWanted,
        },
        time_wanted: null,
      },
    });
    yield put(orderStatusActions.orderPending());

    // Update handoff details
    yield put(
      actions.handoffDetailsSet(CONVEYANCE_TYPES.DELIVERY, {
        instructions: action.instructions ?? '',
      }),
    );
  } catch (error) {
    // See conveyanceSetSaga: recover a transient gateway 502 with the cart-preserving
    // 502 modal instead of the terminal handoff-mode throw at submit.
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield* surfaceConveyanceGatewayFailure(error);
      return;
    }
    yield put(actions.handleConveyanceFailure(error as Error, CONVEYANCE_TYPES.DELIVERY));
  }
}

function* handleConveyanceFailureSaga(action: ReturnType<typeof actions.handleConveyanceFailure>) {
  // Place order in pending state
  yield put(orderStatusActions.orderPending());

  try {
    /**
     * TEAM-13882: the curbside sentence goes in as the static message so the render rule can use it
     * as its tier-4 fallback. Prefixing it onto the result afterwards would now read "Oops. There
     * was an issue setting your curbside details. Something went wrong. Please try again." whenever
     * nothing more specific resolved.
     */
    const staticMessage =
      action.conveyanceType === API_CONVEYANCE_TYPES.CURBSIDE
        ? 'Oops. There was an issue setting your curbside details.'
        : '';

    // Assemble error message
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      staticMessage,
      action.error,
    );

    // Show error
    yield put(orderStatusActions.orderStatusMessageErrorSet(errorResponse.message));
  } catch (error) {
    ErrorReporter.captureException(error, 'error', {
      details: (error as Error).message,
      eventName: ERROR_MESSAGES.SET_CONVEYANCE_ERROR_ERROR,
    });
  }
}

function* clearLockedConveyanceModeSaga() {
  /**
   * In order to clear a locked in conveyance mode, we need to do several things:
   *
   * 1. Clear out persistent route parameters otherwise they will keep informing the
   *    feature flagging system to keep certain features locked down
   * 2. Reset conveyanceMode to pickup
   * 3. Switch the checkout basket fulfillment to pickup
   * 4. Update the basket fulfillment to pickup in Redux as well as localStorage
   * 5. re-generate the feature bag
   * 6. Finally, clear the route parameters from the URL
   */
  yield put(globalActions.clearPersistentParameters());
  yield take([globalActions.CLEAR_PERSISTENT_PARAMETERS_SUCCESS]);

  yield put(actions.toggleHandoff(CONVEYANCE_TYPES.PICKUP));
  // No `conveyanceSet()` here. It flipped orderStatus to UPDATING /
  // CONVEYANCE_SETTING one line before dispatching CONVEYANCE_MODE_SET, which
  // basketMutationGuard now guards — the saga would defer its own next dispatch
  // behind a status nothing in this path ever clears. `conveyanceSetSaga` sets
  // and clears that status itself when a basket exists (TEAM-14696).
  yield put(
    actions.conveyanceModeSet({
      type: API_CONVEYANCE_TYPES.PICKUP,
      details: [],
    }),
  );
  yield put(
    basketActions.updateBasketFulfillment({
      type: CONVEYANCE_TYPES.PICKUP,
    }),
  );
  yield put(globalActions.generateFeatureBag());

  // Clear URL parameters
  const state: RootState = yield select();

  if (!Object.keys(state.app.global.routerStatus.persistentParameters).length) {
    void Router.push(ROUTES.CHECKOUT);
  }
}

// @IMPLEMENTATION-TODO:
// remove in favor of hooks in `<FeatureAccessor />` after Next upgrade
function* handoffTypeToggleSaga() {
  yield put(globalActions.generateFeatureBag());
}

export default function* rootSaga() {
  yield takeLatest(actions.CHECK_DELIVERY_ADDRESS, checkDeliveryAddressSaga);
  yield takeLatest(actions.SET_DELIVERY_ADDRESS, setDeliveryAddressSaga);
  yield takeLatest(actions.CLEAR_DELIVERY_ADDRESS, clearDeliveryAddressSaga);
  yield takeLatest(actions.SET_WANTED_TIME, setWantedTimeSaga);
  yield takeLatest(actions.HANDLE_CONVEYANCE_FAILURE, handleConveyanceFailureSaga);
  yield takeLatest(actions.RESET_LOCKED_CONVEYANCE_MODE, clearLockedConveyanceModeSaga);
  yield takeLatest(actions.HANDOFF_TYPE_TOGGLE, handoffTypeToggleSaga);
  yield takeLatest(actions.CONVEYANCE_MODE_SET, conveyanceSetSaga);
  yield takeLatest(orderStatusActions.CONVEYANCE_DELIVERY_SET, conveyanceDeliverySetSaga);
}
